import { ReactNode, useState } from 'react';
import { ChevronDown, ChevronRight, Plus } from 'lucide-react';
import { Button } from '@hiring-intelligence/ui';

interface AssociationCardProps {
	title: string;
	count?: number;
	onAdd?: () => void;
	/** Custom header action (e.g. a dropdown). Takes precedence over the default +Add button. */
	action?: ReactNode;
	viewAllHref?: string;
	emptyMessage?: string;
	children?: ReactNode;
	defaultOpen?: boolean;
	icon?: ReactNode;
}

/**
 * Right-rail "Deals (3)" / "Companies (1)" / "Contacts (5)" / "Attachments"
 * card with collapsible body and + Add affordance. Mirrors HubSpot's
 * association cards on the right of a record.
 */
export function AssociationCard({
	title,
	count,
	onAdd,
	action,
	viewAllHref,
	emptyMessage = 'No associations yet.',
	children,
	defaultOpen = true,
	icon
}: AssociationCardProps) {
	const [open, setOpen] = useState(defaultOpen);
	const hasContent = !!children;
	return (
		<div className="rounded-lg border bg-card">
			<div className="flex items-center justify-between px-3 py-2 border-b">
				<button
					type="button"
					onClick={() => setOpen((o) => !o)}
					className="flex items-center gap-1.5 text-sm font-semibold text-foreground"
				>
					{open
						? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
						: <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
					{icon && <span className="text-muted-foreground">{icon}</span>}
					<span>{title}</span>
					{typeof count === 'number' && (
						<span className="text-muted-foreground">({count})</span>
					)}
				</button>
				{action ? action : onAdd && (
					<Button
						variant="ghost"
						size="sm"
						className="h-7 px-2 text-xs font-medium text-primary hover:bg-primary/10 hover:text-primary"
						onClick={onAdd}
					>
						<Plus className="mr-1 h-3.5 w-3.5" /> Add
					</Button>
				)}
			</div>
			{open && (
				<div className="space-y-2 p-3">
					{hasContent ? children : (
						<p className="text-xs text-muted-foreground text-center py-3">{emptyMessage}</p>
					)}
					{viewAllHref && hasContent && (
						<a
							href={viewAllHref}
							className="block pt-2 mt-1 border-t text-xs font-medium text-primary hover:underline"
						>
							View all associated {title.toLowerCase()}
						</a>
					)}
				</div>
			)}
		</div>
	);
}

/** Compact item used inside AssociationCard — a clickable row with title + meta lines. */
export function AssociationItem({
	href,
	title,
	meta,
	badge,
	onClick
}: {
	href?: string;
	title: ReactNode;
	meta?: ReactNode;
	badge?: ReactNode;
	onClick?: () => void;
}) {
	const inner = (
		<div className="space-y-0.5">
			<div className="flex items-center justify-between gap-2">
				<div className="min-w-0 truncate text-sm font-medium text-primary">{title}</div>
				{badge}
			</div>
			{meta && <div className="text-xs text-muted-foreground space-y-0.5">{meta}</div>}
		</div>
	);
	const cls = 'block rounded-md border bg-background p-2.5 hover:bg-muted/40 transition-colors';
	if (href) return <a href={href} className={cls}>{inner}</a>;
	return <button type="button" onClick={onClick} className={`${cls} w-full text-left`}>{inner}</button>;
}
